import { defineStore } from 'pinia';
import { reactive, computed, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { useEntityStore } from './entityStore';
import type { IScope } from '@/types/filterBuilderTypes';
import type { SortDirection } from '@/types/tableTypes';
import { useRoute } from 'vue-router';
import { deepClone } from '@/utils/object';
import {
  AUDIO_FILE_ISSUES_ENTITY_ID,
  AUDIO_FILE_ISSUE_COLUMNS,
  AUDIO_FILE_ISSUE_DEFAULT_VISIBLE,
  AUDIO_FILE_ISSUE_DEFAULT_WIDTHS,
} from '@/composables/audioFileIssuesSchema';
import {
  PLAYLIST_SWITCHING_ENTITY_ID,
  PLAYLIST_SWITCHING_COLUMNS,
  PLAYLIST_SWITCHING_DEFAULT_VISIBLE,
  PLAYLIST_SWITCHING_DEFAULT_WIDTHS,
  PLAYLIST_SWITCHING_DEFAULT_SORT,
} from '@/composables/playlistSwitchingSchema';
import { isPageLevelEntityId, resolvePageLevelEntityId } from '@/composables/pageLevelTableEntities';
import { isPhoneScreenNow } from '@/composables/useIsPhone';
import { hasMigratedSavedViews } from '@/utils/savedViewMigration';

/**
 * Snapshot of every column-related field that the column-settings inspector
 * edits. Keyed the same way the live TableState column maps are:
 * order/hidden/pin/size by column `id`, sort by column `field`.
 */
export interface ColumnConfigSnapshot {
  columnOrder: Record<string, number>;
  columnSort: Record<string, SortDirection>;
  columnHidden: Record<string, boolean>;
  columnPin: Record<string, boolean>;
  columnSize: Record<string, number>;
}

/**
 * A named, recallable "view" of a table — captures filter rules + column
 * arrangement together so switching views applies both at once. Replaces the
 * earlier split between `SavedFilter` and `SavedColumnConfig`: a single
 * concept, a single chevron menu, less to think about.
 */
/**
 * The filter half of a saved view. Standalone rather than an indexed access off
 * a `SavedView` interface, because saved views now live on the server (SAU-310)
 * and this store no longer owns their record shape — but the pruned-definition
 * codec still has to name the same filter type the live state uses.
 */
export interface SavedViewFilter {
  ruleScopes: IScope;
  geoRadius?: GeoRadiusFilter;
}

/**
 * The layout axes a saved view captures alongside filter + columns. Optional
 * wherever it appears: views saved before layout capture existed have none and
 * must load without touching the live layout.
 */
export interface SavedViewLayout {
  tableLayout: TableLayoutMode;
  searchViewMode: SearchViewMode;
  // A view saved while the row-click setting existed also holds
  // `rowClickAction`. It is ignored: a row click always shows the list's pane.
}

/**
 * What the CARD layouts show, as opposed to what the table shows.
 *
 * A saved view already restored `tableLayout`, so loading a view saved in cards
 * mode put you back in cards — showing whatever fields that browser happened to
 * have on, which is not the view you saved. These are the two pieces of state
 * the Column Settings inspector edits that the definition did not model.
 */
export interface SavedViewCardFields {
  /** Absent means "leave the card fields alone". `{}` means "show the defaults". See expandCardFields. */
  cardColumnVisible?: Record<string, boolean>;
  /** Absent means the same for the map pins. */
  mapCardFieldVisibility?: Record<MapCardFieldKey, boolean>;
}

/**
 * How the Column settings panel lists its hidden columns. It never changes the
 * table itself.
 *
 * 'custom' shows the hidden columns in two groups, "Fields" and "Related
 * lists". 'alphabetical' shows them as one list from A to Z. The stored values
 * are older than the groups: 'custom' used to mean "in drag order". They were
 * kept so a choice already saved in someone's browser still reads correctly.
 */
export type ColumnListSortMode = 'custom' | 'alphabetical';

export type SearchViewMode = 'list' | 'map';

/**
 * Body-render layout for table-driven pages. `rows` keeps the standard
 * `<DataTable>` (one row per record), `cards` swaps to a responsive grid of
 * inspector-style cards (one card per record), `fixed-cards` renders the
 * entity's fixed card (e.g. the Live Track preview) with no dynamic column
 * fields — only offered for entities that have a fixed card header. Stored
 * per entity scope so each page can remember its preference independently
 * from `searchViewMode` (which is reserved for table-vs-map semantics on the
 * search page).
 */
export type TableLayoutMode = 'rows' | 'cards' | 'fixed-cards';

export interface GeoRadiusPin {
  latitude: number;
  longitude: number;
  radiusMeters: number;
}

export interface GeoRadiusFilter {
  pins: GeoRadiusPin[];
}

/** Visibility of address lines on search map result cards (per entity table state). Name line is always shown. */
export type MapCardFieldKey =
  | 'address1'
  | 'address2'
  | 'city'
  | 'stateSubdivision'
  | 'zipPostal'
  | 'country';

export const DEFAULT_MAP_CARD_FIELD_VISIBILITY: Record<MapCardFieldKey, boolean> = {
  address1: true,
  address2: true,
  city: true,
  stateSubdivision: true,
  zipPostal: true,
  country: true,
};

const MAP_CARD_FIELD_KEYS = Object.keys(DEFAULT_MAP_CARD_FIELD_VISIBILITY) as MapCardFieldKey[];

function normalizeMapCardFieldVisibility(
  raw: Record<string, boolean> | undefined | null,
): Record<MapCardFieldKey, boolean> {
  const out = { ...DEFAULT_MAP_CARD_FIELD_VISIBILITY };
  if (!raw) {
    return out;
  }
  for (const key of MAP_CARD_FIELD_KEYS) {
    if (key in raw && typeof raw[key] === 'boolean') {
      out[key] = raw[key];
    }
  }
  return out;
}

interface TableState {
  columnOrder: Record<string, number>;
  columnSort: Record<string, SortDirection>;
  columnHidden: Record<string, boolean>;
  /**
   * Card-layout column visibility, SEPARATE from the table's columnHidden and
   * opt-IN: a column shows on cards only when explicitly true here, or when it
   * is one of the entity's card DEFAULTS (cardDefaultColumnIdsFor) and has no
   * entry. Cards therefore start minimal — fixed header + defaults — and the
   * user turns extra columns on per entity without touching the table layout.
   */
  cardColumnVisible?: Record<string, boolean>;
  columnPin: Record<string, boolean>;
  columnSize: Record<string, number>;
  pageSize: number;
  pageIndex: number;
  searchViewMode: SearchViewMode;
  /**
   * Body layout (`rows` = DataTable, `cards` = inspector-style card grid).
   * Independent from `searchViewMode` so a page that doesn't have a map view
   * (e.g. audio-file-issues) can still toggle between rows and cards.
   */
  tableLayout: TableLayoutMode;
  /**
   * True once the person has picked a layout for this list, or opened a saved
   * view that sets one. Until then `tableLayout` holds only the starting value,
   * and the list opens in its default layout instead. On a phone that default
   * is cards. See `defaultLayoutFor` in the store.
   *
   * Absent in state stored before this field existed. A stored card layout
   * from then still counts as a choice, because the starting value was always
   * the table.
   */
  tableLayoutChosen?: boolean;
  /** Map view: which physical-address lines to show on result cards (name is always shown; list view uses column settings). */
  mapCardFieldVisibility: Record<MapCardFieldKey, boolean>;
  filter: {
    ruleScopes: IScope;
    geoRadius?: GeoRadiusFilter;
  };
  // The saved views themselves are NOT here any more. They are server rows as of
  // SAU-310, read through useSavedViewsApi and keyed by entity.
  //
  // Which view is currently APPLIED to this table is still live table state, so
  // it stays — as a server id now, not a local string. Drives the chevron menu's
  // checkmark and the control's primary label; unaffected by live edits, so a
  // user can keep treating their loaded view as "the one I'm editing".
  activeSavedViewId: number | null;
  /** Inspector-only: whether the Column settings panel groups its hidden columns or lists them A–Z. */
  columnListSortMode: ColumnListSortMode;
  /**
   * The column the list is grouped by, as its filter field, or null for no
   * grouping (SAU-437). Kept per entity. Saved into saved views as `groupBy`.
   */
  groupBy?: string | null;
}

const DEFAULT_TABLE_STATE: TableState = {
  columnOrder: {},
  columnSort: {},
  columnHidden: {},
  cardColumnVisible: {},
  columnPin: {},
  columnSize: {},
  pageSize: 20,
  pageIndex: 0,
  searchViewMode: 'list',
  tableLayout: 'rows',
  mapCardFieldVisibility: { ...DEFAULT_MAP_CARD_FIELD_VISIBILITY },
  filter: {
    ruleScopes: {
      id: 'root',
      rules: [],
      scopeType: 'and' as const,
    } as IScope,
  },
  activeSavedViewId: null,
  columnListSortMode: 'custom',
  groupBy: null,
};

/**
 * localStorage / migrations can leave null or non-objects on nested fields —
 * fixes Object.keys etc. on consumers. Also drops the legacy `savedFilters` /
 * `savedColumnConfigs` / `activeSavedFilterId` / `activeSavedColumnConfigId`
 * fields from prior persisted state: the unified Views model replaces them
 * and we intentionally don't migrate the contents (users start fresh).
 */
function normalizeTableState(state: TableState): void {
  if (!state.columnOrder || typeof state.columnOrder !== 'object' || Array.isArray(state.columnOrder)) {
    state.columnOrder = {};
  }
  if (!state.columnSort || typeof state.columnSort !== 'object' || Array.isArray(state.columnSort)) {
    state.columnSort = {};
  }
  if (!state.columnHidden || typeof state.columnHidden !== 'object' || Array.isArray(state.columnHidden)) {
    state.columnHidden = {};
  }
  if (!state.columnPin || typeof state.columnPin !== 'object' || Array.isArray(state.columnPin)) {
    state.columnPin = {};
  }
  if (!state.columnSize || typeof state.columnSize !== 'object' || Array.isArray(state.columnSize)) {
    state.columnSize = {};
  }
  if (state.columnListSortMode !== 'custom' && state.columnListSortMode !== 'alphabetical') {
    state.columnListSortMode = 'custom';
  }
  if (typeof state.groupBy !== 'string') {
    state.groupBy = null;
  }
  // The row-click setting is gone. A browser that stored it drops it here.
  delete (state as TableState & { rowClickAction?: unknown }).rowClickAction;
  if (!state.filter || typeof state.filter !== 'object' || Array.isArray(state.filter)) {
    state.filter = deepClone(DEFAULT_TABLE_STATE.filter);
  } else if (
    !state.filter.ruleScopes ||
    typeof state.filter.ruleScopes !== 'object' ||
    Array.isArray(state.filter.ruleScopes)
  ) {
    state.filter.ruleScopes = deepClone(DEFAULT_TABLE_STATE.filter.ruleScopes);
  }
  // Drop legacy fields if a prior persisted state had them. Treating them as
  // any-typed bag so the assignment compiles even though the new TableState
  // no longer declares them.
  const legacy = state as unknown as Record<string, unknown>;
  if ('savedFilters' in legacy) delete legacy.savedFilters;
  if ('activeSavedFilterId' in legacy) delete legacy.activeSavedFilterId;
  if ('savedColumnConfigs' in legacy) delete legacy.savedColumnConfigs;
  if ('activeSavedColumnConfigId' in legacy) delete legacy.activeSavedColumnConfigId;
  // SAU-310: saved views moved to the server. Deleting by NAME matters — the
  // persist plugin picks the whole `tableStates` object, so a field merely
  // dropped from the interface would sit in every existing browser's
  // localStorage forever, invisible to TypeScript and re-persisted on every
  // write. The migration reads these two keys BEFORE anything calls this, which
  // is why nothing may touch a scope until the migration flag is set.
  // SAU-310: `savedViews` moved to the server, and this drop is GATED on the
  // migration flag. It has to be.
  //
  // normalizeTableState runs from readState() on the first render of any table
  // page; the migration runs on the first SUCCESSFUL saved-views query, a
  // network round trip later. Dropping unconditionally therefore destroys the
  // views before the upload ever reads them — and if that first request fails
  // (backend deployed late, expired token, a blip) the stripped blob is already
  // persisted, so every later load finds nothing to migrate and nothing to back
  // up. Once the flag is set the field is genuinely dead and this is tidy-up.
  // The free in-memory check first, deliberately: hasMigratedSavedViews() reads
  // localStorage, and this runs on the read path for every scope. A scope with no
  // legacy `savedViews` key — every scope, once the one-time delete has run — now
  // short-circuits before touching storage.
  if ('savedViews' in legacy && hasMigratedSavedViews()) {
    delete legacy.savedViews;
  }
  // activeSavedViewId SURVIVES, but its type changed: local string ids became
  // server integers. Anything that is not a number is cleared rather than
  // deleted, which retires every pre-migration id without a second delete line.
  if (typeof state.activeSavedViewId !== 'number') {
    state.activeSavedViewId = null;
  }
}


/**
 * The schedule search scope. A model search scope is keyed by its models.json
 * name, so this is the entity id the entity picker gives schedules.
 */
export const CUSTOM_PLAYLIST_SCHEDULE_ENTITY_ID = 'LocationZoneCustomPlaylistSchedule';

/**
 * Schedule columns that models.json lists but the search document does not
 * carry. Each would be blank on every row, so a new scope hides them.
 */
const CUSTOM_PLAYLIST_SCHEDULE_DEFAULT_HIDDEN = [
  'custom_playlist_schedule_type',
  'station_change_location_zone_player',
  'deleted_at',
] as const;

/**
 * Whether a list can show cards. Every searchable record type can, and so can
 * Audio File Issues. Track Matching and Playlist Switching have a table only.
 */
function scopeHasCardLayout(entityId: string): boolean {
  if (!entityId) {
    return false;
  }
  return !isPageLevelEntityId(entityId) || entityId === AUDIO_FILE_ISSUES_ENTITY_ID;
}

export const usePersistentTableStore = defineStore('persistentTableStore', () => {
  const entityStore = useEntityStore();
  const tableStates = reactive<Record<string, TableState>>({});
  const route = useRoute();


  // Track Matching scopes its table state under its own id so it does not overwrite
  // the LiveTrack state the Search page uses. It is a page-level scope like the other
  // two, and resolvePageLevelEntityId knows about all three, so it no longer needs a
  // branch of its own here.
  const selectedEntityId = computed(() => {
    const routeName = typeof route.name === 'string' ? route.name : '';
    return resolvePageLevelEntityId(routeName) ?? entityStore.selectedEntityId;
  });

  /**
   * Seed an audio-file-issues table-state with the default visible / hidden
   * column set so the first-load layout matches the legacy page. We can't bake
   * this into {@link DEFAULT_TABLE_STATE} because that constant is shared
   * across all entity scopes.
   */
  const seedAudioFileIssuesDefaults = (state: TableState) => {
    if (Object.keys(state.columnHidden).length === 0) {
      const hidden: Record<string, boolean> = {};
      for (const column of AUDIO_FILE_ISSUE_COLUMNS) {
        hidden[column.id] = !AUDIO_FILE_ISSUE_DEFAULT_VISIBLE.has(column.id);
      }
      state.columnHidden = hidden;
    }
    if (Object.keys(state.columnSize).length === 0) {
      state.columnSize = { ...AUDIO_FILE_ISSUE_DEFAULT_WIDTHS };
    }
    if (Object.keys(state.columnSort).length === 0) {
      state.columnSort = { severity: 'desc' };
    }
  };

  /**
   * First-load defaults for the schedule search. Station changes are type 3.
   * They are hidden by a rule the user can see and delete, not by the page.
   * The seeder runs only when the scope is created, so a deleted rule stays deleted.
   */
  const seedCustomPlaylistScheduleDefaults = (state: TableState) => {
    const rootScope = state.filter.ruleScopes;
    if ('rules' in rootScope && rootScope.rules.length === 0) {
      rootScope.rules.push({
        id: 'default-hide-station-changes',
        column: 'custom_playlist_schedule_type_id',
        criterion: 'not_contains_any',
        value: ['3'],
        field: null,
      });
    }
    if (Object.keys(state.columnHidden).length === 0) {
      const hidden: Record<string, boolean> = {};
      for (const columnId of CUSTOM_PLAYLIST_SCHEDULE_DEFAULT_HIDDEN) {
        hidden[columnId] = true;
      }
      state.columnHidden = hidden;
    }
  };

  /** First-load defaults for the Playlists report (see the audio-file-issues seeder above). */
  const seedPlaylistSwitchingDefaults = (state: TableState) => {
    if (Object.keys(state.columnHidden).length === 0) {
      const hidden: Record<string, boolean> = {};
      for (const column of PLAYLIST_SWITCHING_COLUMNS) {
        hidden[column.id] = !PLAYLIST_SWITCHING_DEFAULT_VISIBLE.has(column.id);
      }
      state.columnHidden = hidden;
    }
    if (Object.keys(state.columnSize).length === 0) {
      state.columnSize = { ...PLAYLIST_SWITCHING_DEFAULT_WIDTHS };
    }
    if (Object.keys(state.columnSort).length === 0) {
      state.columnSort = { ...PLAYLIST_SWITCHING_DEFAULT_SORT };
    }
  };

  /**
   * The state a scope starts with the first time it is opened: the shared
   * defaults, plus the seeded defaults of the three pages that have their own.
   * Reading a new scope, importing a view into one and "Reset columns" all
   * start from here, so the three cannot drift apart.
   */
  const freshScopeState = (entityId: string): TableState => {
    const state = deepClone(DEFAULT_TABLE_STATE);
    if (entityId === AUDIO_FILE_ISSUES_ENTITY_ID) {
      seedAudioFileIssuesDefaults(state);
    } else if (entityId === PLAYLIST_SWITCHING_ENTITY_ID) {
      seedPlaylistSwitchingDefaults(state);
    } else if (entityId === CUSTOM_PLAYLIST_SCHEDULE_ENTITY_ID) {
      seedCustomPlaylistScheduleDefaults(state);
    }
    return state;
  };

  // The READ path: state for the current entity, creating and repairing it on
  // demand. Never returns undefined, so every computed getter below stays
  // defined. Writes must not go through here — see mutateState.
  const readState = () => {
    const entityId = selectedEntityId.value;
    if (!entityId) {
      // Zero permitted entities (SAU-287): the selection is '' and there is no
      // table to configure. Serve a detached default so the page renders
      // instead of throwing on `.pageSize` of undefined.
      return deepClone(DEFAULT_TABLE_STATE);
    }
    if (!tableStates[entityId]) {
      tableStates[entityId] = freshScopeState(entityId);
    }
    const state = tableStates[entityId];
    if (state.searchViewMode !== 'list' && state.searchViewMode !== 'map') {
      state.searchViewMode = 'list';
    }
    if (
      state.tableLayout !== 'rows' &&
      state.tableLayout !== 'cards' &&
      state.tableLayout !== 'fixed-cards'
    ) {
      state.tableLayout = 'rows';
    }
    if (!state.mapCardFieldVisibility) {
      state.mapCardFieldVisibility = { ...DEFAULT_MAP_CARD_FIELD_VISIBILITY };
    }
    normalizeTableState(state);
    return state;
  };

  /**
   * The WRITE path for whole-value assignment: every computed setter below, and
   * setGeoRadiusFilter, goes through here, so the "no entity selected" case is
   * refused in one visible place instead of seventeen invisible ones.
   *
   * Writes used to run straight through readState(), which with no entity
   * selected hands back a detached copy of the defaults — it accepted the write
   * and then threw the object away. Nothing surfaced: a setting would simply
   * stop saving, with the cause nowhere near the symptom.
   *
   * WHAT THIS DOES NOT COVER: in-place property writes. Callers that reach
   * through a getter and mutate what it returns — `store.columnSort[field] =
   * 'asc'` when a column header is clicked (usePersistentTable.ts),
   * `store.columnSize[id] = size` on a column resize — never touch this function. With no entity selected those
   * still land on a throwaway clone and still vanish silently. Closing that
   * needs the returned object to be frozen or proxied, which would throw on
   * paths that legitimately repair state; it has not been done. Do not read the
   * guard as blanket protection.
   *
   * The whole guarded case is unreachable today: the search page replaces the
   * table entirely when a user can view no models, and the page-level routes
   * always resolve a real entity id. It is guarded rather than assumed because
   * that reachability is a property of the templates, not of this store.
   */
  const mutateState = (mutate: (state: TableState) => void): void => {
    if (!selectedEntityId.value) {
      // A dropped table preference is not worth breaking the page over, so this
      // refuses rather than throws — but refusing quietly is what made the old
      // behaviour so hard to trace. The console line is for whoever is working
      // on it; Sentry is for the case this guard actually exists to catch, a
      // layout change that reaches here in production long after the fact.
      if (import.meta.env.DEV) {
        console.error(
          '[persistentTableStore] Dropped a write: no entity is selected, so there is no table state to persist to.',
        );
      }
      Sentry.captureMessage(
        'persistentTableStore dropped a write: no entity selected',
        'warning',
      );
      return;
    }
    mutate(readState());
  };

  // Computed properties for current entity's state
  const columnOrder = computed<Record<string, number>>({
    get: () => readState().columnOrder,
    set: (value) => {
      mutateState((state) => { state.columnOrder = value; });
    },
  });

  const columnSort = computed<Record<string, SortDirection>>({
    get: () => readState().columnSort,
    set: (value) => {
      mutateState((state) => { state.columnSort = value; });
    },
  });

  const columnHidden = computed<Record<string, boolean>>({
    get: () => readState().columnHidden,
    set: (value) => {
      mutateState((state) => { state.columnHidden = value; });
    },
  });

  const cardColumnVisible = computed<Record<string, boolean>>({
    get: () => {
      const state = readState();
      if (!state.cardColumnVisible) {
        state.cardColumnVisible = {};
      }
      return state.cardColumnVisible;
    },
    set: (value) => {
      mutateState((state) => { state.cardColumnVisible = value; });
    },
  });

  const columnPin = computed<Record<string, boolean>>({
    get: () => readState().columnPin,
    set: (value) => {
      mutateState((state) => { state.columnPin = value; });
    },
  });

  const columnSize = computed<Record<string, number>>({
    get: () => readState().columnSize,
    set: (value: Record<string, number>) => {
      mutateState((state) => { state.columnSize = value; });
    },
  });

  // Pagination computed properties
  const pageSize = computed({
    get: () => readState().pageSize,
    set: (value: number) => {
      mutateState((state) => { state.pageSize = value; });
    },
  });

  const pageIndex = computed({
    get: () => readState().pageIndex,
    set: (value: number) => {
      mutateState((state) => { state.pageIndex = value; });
    },
  });

  const searchViewMode = computed<SearchViewMode>({
    get: () => {
      const mode = readState().searchViewMode;
      return mode === 'map' ? 'map' : 'list';
    },
    set: (value: SearchViewMode) => {
      mutateState((state) => { state.searchViewMode = value; });
    },
  });

  /**
   * Whether the screen was a phone when the current list opened. It is read
   * once each time the list changes, not watched. So resizing the window
   * across `md` does not flip a list that is already open.
   */
  const screenWasPhoneWhenListOpened = ref(isPhoneScreenNow());
  watch(
    () => [selectedEntityId.value, route.name],
    () => { screenWasPhoneWhenListOpened.value = isPhoneScreenNow(); },
  );

  /**
   * The layout a list opens in when the person has not chosen one. On a phone
   * that is cards, if the list has a card layout. Otherwise it is the table.
   * It is only ever read. Writing it would turn a phone visit into a stored
   * choice, and a desktop would then open in cards too.
   */
  const defaultLayoutFor = (entityId: string): TableLayoutMode =>
    screenWasPhoneWhenListOpened.value && scopeHasCardLayout(entityId) ? 'cards' : 'rows';

  const tableLayout = computed<TableLayoutMode>({
    get: () => {
      const state = readState();
      const mode = state.tableLayout;
      // A stored card layout is always a choice: the starting value is the table.
      const hasStoredChoice = state.tableLayoutChosen === true || mode !== 'rows';
      if (!hasStoredChoice) {
        return defaultLayoutFor(selectedEntityId.value);
      }
      return mode === 'cards' || mode === 'fixed-cards' ? mode : 'rows';
    },
    set: (value: TableLayoutMode) => {
      mutateState((state) => {
        state.tableLayout = value;
        state.tableLayoutChosen = true;
      });
    },
  });

  const mapCardFieldVisibility = computed<Record<MapCardFieldKey, boolean>>(() =>
    normalizeMapCardFieldVisibility(readState().mapCardFieldVisibility),
  );

  /** Prefer this over assigning to `mapCardFieldVisibility` — Pinia does not always invoke computed setters from outside the store. */
  function setMapCardFieldVisibility(value: Record<MapCardFieldKey, boolean>): void {
    mutateState((state) => {
      state.mapCardFieldVisibility = normalizeMapCardFieldVisibility(value);
    });
  }

  const filter = computed({
    get: () => readState().filter,
    set: (value: { ruleScopes: IScope; geoRadius?: GeoRadiusFilter }) => {
      mutateState((state) => { state.filter = value; });
    },
  });

  /**
   * Id of the saved view applied to this table, or null. Set when a view is
   * loaded and cleared when it is deleted; live edits do not clear it, which is
   * what lets the control say "Update {name}" against a dirty table.
   */
  const activeSavedViewId = computed<number | null>({
    get: () => readState().activeSavedViewId,
    set: (value) => mutateState((state) => {
      state.activeSavedViewId = value;
    }),
  });

  const groupBy = computed<string | null>({
    get: () => readState().groupBy ?? null,
    set: (value) => {
      mutateState((state) => { state.groupBy = value; });
    },
  });

  const columnListSortMode = computed<ColumnListSortMode>({
    get: () => readState().columnListSortMode,
    set: (value) => {
      mutateState((state) => { state.columnListSortMode = value; });
    },
  });

  /**
   * Put the columns back the way a fresh table has them. This is the Column
   * settings panel's "Reset columns" button.
   *
   * Order and sort go back to the defaults in every layout, because the table
   * and the cards share them. What else is reset depends on the layout:
   *
   * - In the table, which columns are shown and pinned goes back too.
   * - In a card layout, the card's own list of shown fields goes back to the
   *   entity's card defaults. The table's shown and pinned columns are left
   *   alone, because a card layout never edits them.
   *
   * Column widths are not reset. The panel does not edit them, and a reset
   * that also threw away every width someone dragged would be a surprise.
   */
  const resetColumnsToDefaults = (options: { cardLayout: boolean }): void => {
    const defaults = freshScopeState(selectedEntityId.value);
    mutateState((state) => {
      state.columnOrder = defaults.columnOrder;
      state.columnSort = defaults.columnSort;
      if (options.cardLayout) {
        state.cardColumnVisible = {};
        return;
      }
      state.columnHidden = defaults.columnHidden;
      state.columnPin = defaults.columnPin;
    });
  };

  const setGeoRadiusFilter = (pins: GeoRadiusPin[] | null): void => {
    mutateState((state) => {
      const nextFilter = { ...state.filter, ruleScopes: deepClone(state.filter.ruleScopes) };
      if (pins && pins.length > 0) {
        nextFilter.geoRadius = { pins };
      } else {
        delete nextFilter.geoRadius;
      }
      state.filter = nextFilter;
      state.pageIndex = 0;
    });
  };

  // --- Saved views ----------------------------------------------------------
  // Each view bundles a filter snapshot + a column-config snapshot. Mutators
  // always read the live store state — there's no draft / inspector to round
  // through, since the unified toolbar control is the only entry point.

  /**
   * Build a `ColumnConfigSnapshot` from the live committed column state
   * (the store's own columnOrder/columnSort/columnHidden/columnPin/columnSize).
   * Returns a deep clone so callers can mutate without aliasing the store.
   */
  const buildColumnConfigSnapshotFromState = (): ColumnConfigSnapshot => {
    return deepClone({
      columnOrder: columnOrder.value,
      columnSort: columnSort.value,
      columnHidden: columnHidden.value,
      columnPin: columnPin.value,
      columnSize: columnSize.value,
    });
  };

  /** Write a snapshot directly to the live column state. Deep-clones so the snapshot isn't aliased back. */
  const applyColumnConfigSnapshot = (config: ColumnConfigSnapshot): void => {
    const cloned = deepClone(config);
    columnOrder.value = cloned.columnOrder;
    columnSort.value = cloned.columnSort;
    columnHidden.value = cloned.columnHidden;
    columnPin.value = cloned.columnPin;
    columnSize.value = cloned.columnSize;
  };

  /** The layout axes a saved view captures alongside filter + columns. */
  const buildLayoutSnapshot = (): SavedViewLayout => ({
    tableLayout: tableLayout.value,
    searchViewMode: searchViewMode.value,
  });

  /**
   * The card-field half of a view snapshot.
   *
   * Reads `readState().cardColumnVisible ?? {}` rather than going through the
   * `cardColumnVisible` computed: that getter LAZILY WRITES `{}` into the scope
   * when the key is missing (readState repairs mapCardFieldVisibility but not
   * this one), and this runs inside `liveDefinition`, a computed. Reading the
   * raw state keeps a computed from mutating store state on every evaluation.
   */
  const buildCardFieldsSnapshot = (): SavedViewCardFields => deepClone({
    cardColumnVisible: readState().cardColumnVisible ?? {},
    mapCardFieldVisibility: normalizeMapCardFieldVisibility(readState().mapCardFieldVisibility),
  });

  /**
   * Write a card-field snapshot to the live state. REPLACES both maps rather
   * than merging, for the same reason `applyColumnConfigSnapshot` does: a merge
   * would leave the previous view's fields showing through for anything this
   * one does not mention.
   */
  const applyCardFieldsSnapshot = (snapshot: SavedViewCardFields): void => {
    mutateState((state) => {
      // Each half only when present: a view saved before the author customised
      // their cards carries map fields and no card list, and must not blank the
      // reader's cards on the way in.
      if (snapshot.cardColumnVisible) {
        state.cardColumnVisible = deepClone(snapshot.cardColumnVisible);
      }
      if (snapshot.mapCardFieldVisibility) {
        state.mapCardFieldVisibility = normalizeMapCardFieldVisibility(
          snapshot.mapCardFieldVisibility,
        );
      }
    });
  };

  /**
   * Both axes present and spelled correctly. Views built in this browser always
   * are; a shared link is a URL a stranger can edit, so it gets checked before
   * it becomes a stored snapshot (readState only repairs the LIVE state, so a
   * bad value written straight into a scope would persist there unrepaired).
   */
  const isValidLayout = (
    layout: SavedViewLayout | undefined,
  ): layout is SavedViewLayout =>
    !!layout
    && (layout.tableLayout === 'rows'
      || layout.tableLayout === 'cards'
      || layout.tableLayout === 'fixed-cards')
    && (layout.searchViewMode === 'list' || layout.searchViewMode === 'map');

  /**
   * Set (or clear) the active view id for ONE NAMED scope, rather than for
   * whichever scope is routed at the moment the write happens.
   *
   * The `activeSavedViewId` computed's setter goes through `mutateState`, which
   * resolves the scope at CALL time. That is correct from a synchronous handler
   * and wrong from an async one: a save that settles after the user has switched
   * the entity picker stamps the new view's id onto whatever page they moved to.
   * That page's control then offers "Update {a view it does not have}", and the
   * page the view really belongs to still says "Save" — the same mis-scoping
   * `applyDefinitionToScope` exists to prevent, arriving through a callback
   * instead of through a route.
   *
   * A scope the user has never opened is left alone: there is no live state to
   * mark, and seeding a whole scope to record an id would create one without any
   * of that page's defaults.
   */
  const setActiveSavedViewIdForScope = (
    targetEntityId: string,
    savedViewId: number | null,
  ): void => {
    const scope = tableStates[targetEntityId];
    if (!scope) {
      return;
    }
    scope.activeSavedViewId = savedViewId;
  };

  /**
   * Apply a saved view's snapshot to a NAMED entity scope — not necessarily the
   * routed one — and reset paging.
   *
   * The single write path for both ways a view gets applied. `useSavedViews.applyView`
   * passes the routed scope when the user picks a view from the control; the
   * `/views/import` flow passes the view's own entity, because a recipient accepting
   * a share link is on `/views/import`, where `selectedEntityId` points somewhere
   * else entirely and the live computeds would write to the wrong scope. This reaches
   * `tableStates[targetEntityId]` directly, initialising it if the user has never
   * visited that page.
   *
   * It writes ONLY live state. The view itself is a server row now (SAU-310), so
   * there is no local record to create and no id to return — which is also why this
   * replaced `importViewIntoScope`.
   */
  const applyDefinitionToScope = (
    targetEntityId: string,
    filterSnapshot: SavedViewFilter,
    columnConfig: ColumnConfigSnapshot,
    layout?: SavedViewLayout,
    activeSavedViewId: number | null = null,
    cardFields?: SavedViewCardFields,
    groupBy: string | null = null,
  ): void => {
    if (!targetEntityId) {
      // Same refusal as mutateState, and reported for the same reason: without a
      // scope there is nowhere to write, and seeding `tableStates['']` would build a
      // whole phantom scope that persists and that no page ever reads. Refusing
      // quietly is what made the original mis-scoping so hard to trace, so this one
      // is audible — the guarded case is unreachable through today's callers, and it
      // is guarded because that is a property of the templates, not of this store.
      if (import.meta.env.DEV) {
        console.error(
          '[persistentTableStore] Dropped a saved view: no target entity scope was named.',
        );
      }
      Sentry.captureMessage(
        'persistentTableStore dropped a saved view: no target entity scope',
        'warning',
      );

      return;
    }

    if (!tableStates[targetEntityId]) {
      // Every scope readState seeds gets the same defaults here. The old
      // importViewIntoScope seeded audio-file-issues only, so importing into a
      // Playlist Switching scope the user had never opened produced a scope with
      // no default widths, no default hidden set and no default sort.
      tableStates[targetEntityId] = freshScopeState(targetEntityId);
    }
    const scope = tableStates[targetEntityId];
    // The only thing that repairs a scope the user is not routed to — readState
    // never runs for it until they navigate there.
    normalizeTableState(scope);

    // The layout came off the wire, so it is checked rather than trusted.
    // normalizeTableState only repairs the axes it knows about on the LIVE
    // fields, so a bad value written straight in would stick.
    const importedLayout = isValidLayout(layout) ? { ...layout } : undefined;

    scope.filter = deepClone(filterSnapshot);
    scope.columnOrder = deepClone(columnConfig.columnOrder);
    scope.columnSort = deepClone(columnConfig.columnSort);
    scope.columnHidden = deepClone(columnConfig.columnHidden);
    scope.columnPin = deepClone(columnConfig.columnPin);
    scope.columnSize = deepClone(columnConfig.columnSize);
    if (importedLayout) {
      scope.tableLayout = importedLayout.tableLayout;
      // A saved view that sets a layout is the person's choice for this list.
      scope.tableLayoutChosen = true;
      scope.searchViewMode = importedLayout.searchViewMode;
    }
    // expandCardFields always sends a card map. It is `{}`, the default card
    // fields, for a view that stored no card list. Only a caller that passes
    // no card fields at all leaves the cards alone.
    if (cardFields?.cardColumnVisible) {
      scope.cardColumnVisible = deepClone(cardFields.cardColumnVisible);
    }
    if (cardFields?.mapCardFieldVisibility) {
      scope.mapCardFieldVisibility = normalizeMapCardFieldVisibility(
        cardFields.mapCardFieldVisibility,
      );
    }
    // Always written. Null, no grouping, for a view saved before grouping was
    // stored. The caller reads it with expandGroupBy.
    scope.groupBy = groupBy;
    scope.pageIndex = 0;

    // Set explicitly, never left to whatever was there. Two reasons, and both
    // were live bugs:
    //
    // 1. The caller cannot do it. Assigning through the store's computed setter
    //    writes to the CURRENTLY ROUTED scope, and the import page is routed to
    //    /views/import — so it landed on whichever entity the picker happened to
    //    hold, marking a stranger's view active on the wrong page while the view
    //    it actually imported was never marked at all.
    // 2. A stale id left in place is worse than none. If the recipient already
    //    had a view loaded on that page, the control would go on offering
    //    "Update {their view}" while showing the imported content — one click
    //    from overwriting their own view with the stranger's.
    scope.activeSavedViewId = activeSavedViewId;
  };

  /**
   * Drop the retired saved-view keys from EVERY scope in memory, not just the one
   * the user happens to be standing on.
   *
   * The migration strips them from localStorage, but this store was hydrated before
   * that ran, and pinia-plugin-persistedstate rewrites the whole `tableStates`
   * object from IN-MEMORY state on the next mutation. So a column resize put every
   * un-routed scope's views straight back into storage, at full column-snapshot
   * size, right next to the backup copy the migration had just written.
   *
   * normalizeTableState does the same job but only ever reaches the routed scope,
   * because it runs from readState(). That is why this walk cannot live there.
   *
   * Called only after the migration reports success, for the reason
   * normalizeTableState's own drop is gated on the flag: until the upload has
   * actually happened, these keys are the only copy of the user's views.
   */
  const dropMigratedSavedViewKeys = (): void => {
    for (const scope of Object.values(tableStates)) {
      const legacy = scope as unknown as Record<string, unknown>;
      if ('savedViews' in legacy) {
        delete legacy.savedViews;
      }
      // Assigned, not deleted. normalizeTableState puts a missing one back as null
      // on the next read anyway, and a delete here would only make the two
      // disagree about the shape of a scope.
      if (typeof scope.activeSavedViewId !== 'number') {
        scope.activeSavedViewId = null;
      }
    }
  };

  return {
    storeKey: selectedEntityId,
    tableStates,
    // Column state
    columnOrder,
    columnSort,
    columnHidden,
    cardColumnVisible,
    columnPin,
    columnSize,
    // Pagination state
    pageSize,
    pageIndex,
    searchViewMode,
    tableLayout,
    mapCardFieldVisibility,
    setMapCardFieldVisibility,
    // Filter state
    filter,
    setGeoRadiusFilter,
    // Column-list display preference (separate from saved-view machinery)
    columnListSortMode,
    groupBy,
    resetColumnsToDefaults,
    // Saved views (unified filter + column-config presets)
    buildColumnConfigSnapshotFromState,
    applyColumnConfigSnapshot,
    buildCardFieldsSnapshot,
    applyCardFieldsSnapshot,
    activeSavedViewId,
    setActiveSavedViewIdForScope,
    applyDefinitionToScope,
    dropMigratedSavedViewKeys,
    buildLayoutSnapshot,
    isValidLayout,
  };
}, {
  persist: {
    storage: localStorage,
    key: 'persistentTableStore',
    pick: ['tableStates']
  },
});
